import {
  ComponentProps,
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react'
import Modal from '@common/Modal'
import EditStudentModal from '@pages/Admin/AllUsers/modals/EditStudentModal/EditStudentModal'
import EditParentModal from '@pages/Admin/AllUsers/modals/EditParentModal'
import { default as BaseLeadProfileModal } from '@pages/Admin/CRM/components/LeadProfileModal/LeadProfileModal'
import classes from './index.module.css'
import { CRMProvider, useCheckCRMContext } from '@pages/Admin/CRM/CRMContext'
import { Lead } from '@/api/types/crm/crm.types'
import type { LeadsTableRow } from '@pages/Admin/CRM/utils/mapLeadsToTableRows'
import { crmApi } from '@/api'
import cn from 'classnames'
import CollegesItemEdit from '@pages/Admin/AllUsers/Colleges/CollegesItemEdit'

type EditEntityModalProps = EditEntityModalBaseProps & OptionsModalProps

type OptionsModalProps =
  | EditStudentModalProps
  | EditParentModalProps
  | EditLeadModalProps
  | EditEmployeeModalProps

interface EditStudentModalProps {
  type: 'student'
  studentId?: string | number | null
}

interface EditParentModalProps {
  type: 'parent'
  parentId?: string | number | null
}
interface EditEmployeeModalProps {
  type: 'employee'
  employeeId: string | number | null
}

type EditLeadModalProps = {
  type: 'lead'
  /** При `open === false` может быть не задан (см. вкладки воронки). */
  lead?: Lead | LeadsTableRow
  boardFunnelId?: number
}

interface EditEntityModalBaseProps {
  classNames?: {
    studentModal?: string
    studentModalBackground?: string
    parentModal?: string
    parentModalBackground?: string
    employeeModal?: string
    employeeModalBackground?: string
  }
  open: boolean
  onDismiss: () => void
  onUpdate: () => void
  zIndex?: number
  /** Канбан: снять спиннер открытия (данные лида уже в пропе `lead`) */
  onLeadProfileFetchSettled?: () => void
  /** Канбан: точечный рефетч карточки по `lead_funnel_id` после изменений в модалке лида */
  onKanbanCardSync?: (leadFunnelId: number) => void
  /** Таблица лидов воронки: рефетч без закрытия карточки (чат ВК и т.п.) */
  onLeadsListRefetch?: () => void
}

type OpenModalState = OptionsModalProps | { type: null }
type LeadWithCurrentFunnel = (Lead & { currentFunnelId?: number | null }) | LeadsTableRow
type BaseLeadProp = LeadWithCurrentFunnel | undefined

const isSameOpenModalState = (
  prev: OpenModalState,
  next: OpenModalState,
): boolean => {
  if (prev.type !== next.type) return false
  if (prev.type === null && next.type === null) return true

  const prevRecord = prev as Record<string, unknown>
  const nextRecord = next as Record<string, unknown>

  const keys = new Set([
    ...Object.keys(prevRecord),
    ...Object.keys(nextRecord),
  ])

  for (const key of keys) {
    if (prevRecord[key] !== nextRecord[key]) return false
  }

  return true
}

/** Стабильный ключ для смены лида в стеке модалки без полного remount */
const leadModalIdentity = (
  m: { type: 'lead'; lead?: Lead | LeadsTableRow },
): string => {
  const L = m.lead
  if (!L || typeof L !== 'object') return ''
  const row = L as LeadsTableRow
  if (row.rowId != null && row.rowId !== '') return `row:${String(row.rowId)}`
  if (row.lead_funnel_id != null)
    return `lf:${String(row.lead_funnel_id)}`
  if ('id' in L && L.id != null) return `id:${String(L.id)}`
  return ''
}

const EditEntityModal: FC<EditEntityModalProps> = ({
  type,
  open,
  onDismiss,
  onUpdate,
  onKanbanCardSync,
  onLeadProfileFetchSettled,
  onLeadsListRefetch,
  classNames,
  zIndex,
  ...props
}) => {
  const [openModal, setOpenModal] = useState<OpenModalState>({ type: null })
  const modalStyle = zIndex ? { zIndex: String(zIndex) } : undefined
  const [nestedEmployeeId, setNestedEmployeeId] = useState<
    string | number | null
  >(null)

  const memoizedProps = useMemo(() => {
    if (type === 'student' && 'studentId' in props)
      return { studentId: props.studentId }
    if (type === 'parent' && 'parentId' in props)
      return { parentId: props.parentId }
    if (type === 'lead' && 'lead' in props) {
      const boardFunnelId =
        'boardFunnelId' in props ? props.boardFunnelId : undefined
      return {
        ...(props.lead ? { lead: props.lead } : {}),
        ...(typeof boardFunnelId === 'number' && Number.isFinite(boardFunnelId)
          ? { boardFunnelId }
          : {}),
      }
    }
    if (type === 'employee' && 'employeeId' in props)
      return { employeeId: props.employeeId }
    return {}
  }, [
    type,
    type === 'student' ? (props as any).studentId : null,
    type === 'parent' ? (props as any).parentId : null,
    type === 'lead' && 'lead' in props ? props.lead : null,
    type === 'lead' && 'boardFunnelId' in props ? props.boardFunnelId : null,
    type === 'employee' ? (props as any).employeeId : null,
  ])

  useEffect(() => {
    if (!open) {
      setOpenModal({ type: null })
      setNestedEmployeeId(null)
    }
  }, [open])

  useEffect(() => {
    if (!open || !type) return

    const nextState = { type, ...memoizedProps } as OpenModalState

    if (type === 'lead' && (!('lead' in nextState) || !nextState.lead)) return

    const bothLead =
      type === 'lead' &&
      openModal.type === 'lead' &&
      'lead' in nextState &&
      nextState.lead

    if (openModal.type !== null && !bothLead) return

    if (bothLead && openModal.type === 'lead' && 'lead' in openModal && openModal.lead) {
      const prevKey = leadModalIdentity(openModal)
      const nextKey = leadModalIdentity(
        nextState as { type: 'lead'; lead?: Lead | LeadsTableRow },
      )
      if (prevKey !== '' && prevKey === nextKey) {
        setOpenModal((prev) => {
          if (prev.type !== 'lead') return prev
          const merged = { ...prev, ...memoizedProps } as OpenModalState
          return isSameOpenModalState(prev, merged) ? prev : merged
        })
        return
      }
    }

    setOpenModal((prev) =>
      isSameOpenModalState(prev, nextState) ? prev : nextState,
    )
  }, [open, type, memoizedProps, openModal.type])

  const closeModal = useCallback(() => {
    setOpenModal({ type: null })
    onDismiss()
  }, [onDismiss])

  const closeModalAndUpdate = useCallback(() => {
    setOpenModal({ type: null })
    onUpdate()
  }, [onUpdate])

  return (
    <>
      <Modal
        active={open && openModal.type === 'student' && !!openModal.studentId}
        setActive={closeModal}
        className={cn(classes.fullModal, classNames?.studentModal) as any}
        classNameBackground={classNames?.studentModalBackground}
        style={modalStyle}
      >
        {openModal.type === 'student' && openModal.studentId ? (
          <EditStudentModal
            studentId={openModal.studentId}
            onDismiss={closeModal}
            onUpdate={closeModalAndUpdate}
            onClickByTeacher={(teacher: { id?: string | number } | string | number) => {
              const employeeId =
                typeof teacher === 'object' ? teacher?.id : teacher

              if (employeeId) {
                setNestedEmployeeId(employeeId)
              }
            }}
            onClickByParent={(parent: { id: string | number }) =>
              setOpenModal({ type: 'parent', parentId: parent.id })
            }
            onClickByLead={(leadId: string | number) => {
              void crmApi
                .getLead({ lead_id: Number(leadId) })
                .then((res) => {
                  const data = res.data?.data
                  if (data) setOpenModal({ type: 'lead', lead: data })
                })
                .catch(() => console.error('Error fetching lead'))
            }}
          />
        ) : null}
      </Modal>
      <Modal
        active={open && !!nestedEmployeeId}
        setActive={() => setNestedEmployeeId(null)}
        // @ts-ignore
        className={cn(
          classes.fullModal,
          classes.employeeModal,
          classNames?.employeeModal,
        )}
        classNameBackground={classNames?.employeeModalBackground}
      >
        {nestedEmployeeId ? (
          <CollegesItemEdit
            big
            setEdit={() => setNestedEmployeeId(null)}
            edit={nestedEmployeeId}
            setState={null}
          />
        ) : null}
      </Modal>
      <Modal
        active={open && openModal.type === 'parent' && !!openModal.parentId}
        setActive={closeModal}
        className={cn(classes.fullModal, classNames?.parentModal) as any}
        classNameBackground={classNames?.parentModalBackground}
        style={modalStyle}
      >
        {openModal.type === 'parent' && openModal.parentId ? (
          <EditParentModal
            parentId={openModal.parentId as any}
            onDismiss={closeModal}
            onUpdate={closeModalAndUpdate}
            onClickByChildren={
              ((child: { id: string | number }) =>
                setOpenModal({ type: 'student', studentId: child.id })) as any
            }
          />
        ) : null}
      </Modal>
      <LeadProfileModal
        open={open && openModal.type === 'lead'}
        lead={
          openModal.type === 'lead' && 'lead' in openModal
            ? openModal.lead
            : undefined
        }
        boardFunnelId={
          openModal.type === 'lead' && 'boardFunnelId' in openModal
            ? openModal.boardFunnelId
            : undefined
        }
        onKanbanCardSync={onKanbanCardSync}
        onLeadProfileFetchSettled={onLeadProfileFetchSettled}
        onLeadsListRefetch={onLeadsListRefetch}
        onClose={(withChanges) =>
          withChanges ? closeModalAndUpdate() : closeModal()
        }
        onClickByChildren={(userId) =>
          setOpenModal({ type: 'student', studentId: userId })
        }
      />
      <Modal
        active={open && openModal.type === 'employee' && !!openModal.employeeId}
        setActive={closeModal}
        // @ts-ignore
        className={cn(
          classes.fullModal,
          classes.employeeModal,
          classNames?.employeeModal,
        )}
        classNameBackground={classNames?.employeeModalBackground}
        style={modalStyle}
      >
        {openModal.type === 'employee' && openModal.employeeId ? (
          <CollegesItemEdit
            big
            setEdit={closeModal}
            edit={openModal.employeeId}
            setState={null}
          />
        ) : null}
      </Modal>
    </>
  )
}

// ---------- LEAD MODAL -----------

interface LeadProfileModalProps
  extends Omit<ComponentProps<typeof BaseLeadProfileModal>, 'lead'> {
  lead?: LeadWithCurrentFunnel
  boardFunnelId?: number
  onKanbanCardSync?: (leadFunnelId: number) => void
  onLeadProfileFetchSettled?: () => void
}

const isLeadObject = (obj: unknown): obj is LeadWithCurrentFunnel => {
  if (!obj || typeof obj !== 'object') return false
  const candidate = obj as { id?: unknown }
  return typeof candidate.id === 'string' || typeof candidate.id === 'number'
}

const withBoardFunnel = (
  data: BaseLeadProp,
  boardFunnelId?: number,
): BaseLeadProp => {
  if (!data) return undefined
  if (
    boardFunnelId === undefined ||
    !Number.isFinite(boardFunnelId) ||
    boardFunnelId <= 0
  ) {
    return data
  }
  return { ...data, currentFunnelId: boardFunnelId }
}

const LeadProfileModal: FC<LeadProfileModalProps> = ({
  lead: defLead,
  boardFunnelId,
  onKanbanCardSync,
  onLeadProfileFetchSettled,
  ...props
}) => {
  const hasCRMContext = useCheckCRMContext()
  const [lead, setLead] = useState<BaseLeadProp>()
  const fetchSettledRef = useRef(onLeadProfileFetchSettled)
  fetchSettledRef.current = onLeadProfileFetchSettled

  useEffect(() => {
    if (isLeadObject(defLead)) {
      setLead(withBoardFunnel(defLead, boardFunnelId))
      fetchSettledRef.current?.()
      return
    }
    setLead(undefined)
    fetchSettledRef.current?.()
    // Раньше здесь был getLead по id — из-за цепочки setState/await модалка мигала.
    // Открытие только с объектом `lead` (или fetch в onClickByLead у студента).
  }, [defLead, boardFunnelId])

  useEffect(() => {
    // Функция для удаления всех стилей rubberLayout
    const removeAllRubberLayoutStyles = () => {
      // Удаляем класс с body
      const body = document.querySelector('body')
      if (body?.classList.contains('rubberLayout')) {
        body.classList.remove('rubberLayout')
      }
    }

    // Вызываем функцию очистки
    removeAllRubberLayoutStyles()

    // Также устанавливаем интервал для периодической очистки (на случай если стили применяются динамически)
    const intervalId = setInterval(removeAllRubberLayoutStyles, 100)

    // Очищаем интервал при размонтировании
    return () => {
      clearInterval(intervalId)
      removeAllRubberLayoutStyles()
    }
  }, [])

  if (hasCRMContext)
    return lead ? (
      <BaseLeadProfileModal
        {...props}
        lead={lead as any}
        onKanbanCardSync={onKanbanCardSync}
      />
    ) : null

  return (
    <CRMProvider currentTab={lead?.type ?? 'new'}>
      {lead ? (
        <BaseLeadProfileModal
          {...props}
          lead={lead as any}
          onKanbanCardSync={onKanbanCardSync}
        />
      ) : null}
    </CRMProvider>
  )
}

export default EditEntityModal
